import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import { Box, IconButton, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useThemeGreyBackgroundColor } from "hooks/useThemeGreyBackgroundColor";
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type RefObject,
} from "react";

import type { ScoutChatMessage } from "./message/scoutChatMessage";

import { AgentMessage } from "./message/AgentMessage";
import { UserMessage } from "./message/UserMessage";
import { ScoutInput } from "./ScoutInput";

const ScoutDisclaimer = memo(function ScoutDisclaimer() {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: "block", textAlign: "center", mt: 1.5, px: 2 }}
    >
      Scout is AI and can make mistakes. Please double-check responses.
    </Typography>
  );
});
ScoutDisclaimer.displayName = "ScoutDisclaimer";

const ChatFooter = memo(
  function ChatFooter({
    onSubmit,
    onAbort,
    isSending,
    isAborting,
    autoFocus,
  }: {
    onSubmit: (text: string) => Promise<void>;
    onAbort: () => void;
    isSending: boolean;
    isAborting: boolean;
    autoFocus: boolean;
  }) {
    return (
      <Box
        component="footer"
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2,
          pt: 1,
          p: 2,
          pointerEvents: "none",
        }}
      >
        <Box sx={{ pointerEvents: "auto" }}>
          <ScoutInput
            onSubmit={onSubmit}
            onAbort={onAbort}
            isSending={isSending}
            isAborting={isAborting}
            placeholder="Ask Scout a follow-up question"
            autoFocus={autoFocus}
          />
        </Box>
        <ScoutDisclaimer />
      </Box>
    );
  },
  (prev, next) =>
    prev.onSubmit === next.onSubmit &&
    prev.onAbort === next.onAbort &&
    prev.isSending === next.isSending &&
    prev.isAborting === next.isAborting &&
    prev.autoFocus === next.autoFocus
);
ChatFooter.displayName = "ChatFooter";

export function ChatChrome({
  messages,
  loading = false,
  initialPinned = false,
  onSubmit,
  onAbort,
  isSending,
  isAborting,
  onLoadOlderMessages,
  isLoadingOlderMessages = false,
  hasOlderMessages = false,
  showFooter = true,
  inputAutoFocus = true,
}: {
  messages: ScoutChatMessage[];
  loading?: boolean;
  initialPinned?: boolean;
  onSubmit: (text: string) => Promise<void>;
  onAbort: () => void;
  isSending: boolean;
  isAborting: boolean;
  onLoadOlderMessages?: () => Promise<void>;
  isLoadingOlderMessages?: boolean;
  hasOlderMessages?: boolean;
  showFooter?: boolean;
  inputAutoFocus?: boolean;
}) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const userScrollIntentRef = useRef(false);
  const didApplyInitialPinRef = useRef(false);
  const [isPinned, setIsPinned] = useState(initialPinned);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);

  const updateJumpToLatestState = useCallback(
    (nextIsPinned = isPinned) => {
      const scrollContainer = scrollContainerRef.current;
      if (!scrollContainer) {
        setShowJumpToLatest(false);
        return;
      }

      const hasScrollableContent = scrollContainer.scrollHeight > scrollContainer.clientHeight + 1;
      setShowJumpToLatest(hasScrollableContent && !nextIsPinned);
    },
    [isPinned]
  );

  const scrollToLatest = useCallback((behavior: ScrollBehavior = "auto") => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      return;
    }

    scrollContainer.scrollTo({
      top: scrollContainer.scrollHeight,
      behavior,
    });
  }, []);

  const handleJumpToLatest = useCallback(() => {
    setIsPinned(true);
    setShowJumpToLatest(false);
    requestAnimationFrame(() => {
      scrollToLatest("smooth");
    });
  }, [scrollToLatest]);

  useEffect(() => {
    if (!isPinned) {
      updateJumpToLatestState();
      return;
    }

    requestAnimationFrame(() => {
      scrollToLatest();
      updateJumpToLatestState();
    });
  }, [isPinned, messages, scrollToLatest, updateJumpToLatestState]);

  useEffect(() => {
    if (!initialPinned || didApplyInitialPinRef.current) {
      return;
    }

    didApplyInitialPinRef.current = true;
    setIsPinned(true);
    requestAnimationFrame(() => {
      scrollToLatest();
      updateJumpToLatestState(true);
    });
  }, [initialPinned, scrollToLatest, updateJumpToLatestState]);

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        position: "relative",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <MessageList
        messages={messages}
        loading={loading}
        isPinned={isPinned}
        showFooter={showFooter}
        scrollContainerRef={scrollContainerRef}
        userScrollIntentRef={userScrollIntentRef}
        onScrollStateChange={updateJumpToLatestState}
        onPin={() => setIsPinned(true)}
        onUnpin={() => setIsPinned(false)}
        onLoadOlderMessages={onLoadOlderMessages}
        isLoadingOlderMessages={isLoadingOlderMessages}
        hasOlderMessages={hasOlderMessages}
      />
      <BottomCanvasFade />
      <Stack
        direction="row"
        justifyContent="center"
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: showFooter ? 116 : 16,
          zIndex: 3,
          opacity: showJumpToLatest ? 1 : 0,
          pointerEvents: showJumpToLatest ? "auto" : "none",
          transition: "opacity 120ms ease",
        }}
      >
        <Tooltip title="Jump to latest">
          <IconButton
            aria-label="Jump to latest Scout message"
            color="primary"
            onClick={handleJumpToLatest}
            size="small"
            sx={{
              bgcolor: "background.paper",
              border: 1,
              borderColor: "divider",
              boxShadow: 1,
              "&:hover": {
                bgcolor: "background.paper",
              },
            }}
          >
            <KeyboardArrowDown />
          </IconButton>
        </Tooltip>
      </Stack>
      {showFooter && (
        <ChatFooter
          onSubmit={onSubmit}
          onAbort={onAbort}
          isSending={isSending}
          isAborting={isAborting}
          autoFocus={inputAutoFocus}
        />
      )}
    </Box>
  );
}

function BottomCanvasFade() {
  const canvasColor = useThemeGreyBackgroundColor();

  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        left: 0,
        right: "16px",
        bottom: 0,
        zIndex: 1,
        height: { xs: 240, sm: 224 },
        pointerEvents: "none",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          maxWidth: "1000px",
          width: "100%",
          height: "100%",
          background: `linear-gradient(to bottom, ${alpha(
            canvasColor,
            0
          )} 0%, ${alpha(canvasColor, 0.9)} 48%, ${canvasColor} 72%)`,
        }}
      />
    </Box>
  );
}

function MessageList({
  messages,
  loading,
  isPinned,
  showFooter,
  scrollContainerRef,
  userScrollIntentRef,
  onScrollStateChange,
  onPin,
  onUnpin,
  onLoadOlderMessages,
  isLoadingOlderMessages,
  hasOlderMessages,
}: {
  messages: ScoutChatMessage[];
  loading: boolean;
  isPinned: boolean;
  showFooter: boolean;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  userScrollIntentRef: RefObject<boolean>;
  onScrollStateChange: (nextIsPinned?: boolean) => void;
  onPin: () => void;
  onUnpin: () => void;
  onLoadOlderMessages?: () => Promise<void>;
  isLoadingOlderMessages: boolean;
  hasOlderMessages: boolean;
}) {
  const theme = useTheme();
  const canvasColor = useThemeGreyBackgroundColor();
  const scrollbarThumbColor = alpha(
    theme.palette.text.primary,
    theme.palette.mode === "light" ? 0.24 : 0.34
  );
  const userScrollIntentTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const hasTriggeredTopFetchRef = useRef(false);
  const prependAnchorRef = useRef<{ scrollTop: number; scrollHeight: number } | null>(null);
  const TOP_FETCH_THRESHOLD_PX = 80;

  const clearUserScrollIntent = useCallback(() => {
    if (userScrollIntentTimeoutRef.current) {
      clearTimeout(userScrollIntentTimeoutRef.current);
      userScrollIntentTimeoutRef.current = undefined;
    }

    userScrollIntentRef.current = false;
  }, [userScrollIntentRef]);

  const markUserScrollIntent = useCallback(() => {
    if (userScrollIntentTimeoutRef.current) {
      clearTimeout(userScrollIntentTimeoutRef.current);
    }

    userScrollIntentRef.current = true;
    userScrollIntentTimeoutRef.current = setTimeout(() => {
      userScrollIntentRef.current = false;
      userScrollIntentTimeoutRef.current = undefined;
    }, 750);
  }, [userScrollIntentRef]);

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent) => {
      if (
        event.key === "ArrowUp" ||
        event.key === "PageUp" ||
        event.key === "Home" ||
        (event.key === " " && event.shiftKey)
      ) {
        markUserScrollIntent();
      }
    },
    [markUserScrollIntent]
  );

  const handleScroll = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      return;
    }

    const isNearTop = scrollContainer.scrollTop <= TOP_FETCH_THRESHOLD_PX;
    if (!isNearTop) {
      hasTriggeredTopFetchRef.current = false;
    } else if (
      hasOlderMessages &&
      !isLoadingOlderMessages &&
      onLoadOlderMessages &&
      !hasTriggeredTopFetchRef.current
    ) {
      hasTriggeredTopFetchRef.current = true;
      prependAnchorRef.current = {
        scrollTop: scrollContainer.scrollTop,
        scrollHeight: scrollContainer.scrollHeight,
      };
      void onLoadOlderMessages();
    }

    const distanceFromBottom =
      scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight;
    const isAtBottom = distanceFromBottom <= 24;
    const didUserScroll = userScrollIntentRef.current;
    const didUserPin = isAtBottom && didUserScroll;
    const didUserUnpin = isPinned && !isAtBottom && didUserScroll;

    if (didUserPin) {
      onPin();
      clearUserScrollIntent();
    }

    if (didUserUnpin) {
      onUnpin();
      clearUserScrollIntent();
    }

    if (didUserPin) {
      onScrollStateChange(true);
      return;
    }

    onScrollStateChange(didUserUnpin ? false : undefined);
  }, [
    clearUserScrollIntent,
    hasOlderMessages,
    isLoadingOlderMessages,
    isPinned,
    onLoadOlderMessages,
    onPin,
    onScrollStateChange,
    onUnpin,
    scrollContainerRef,
    userScrollIntentRef,
  ]);

  useEffect(() => {
    onScrollStateChange();
  }, [messages, onScrollStateChange]);

  useLayoutEffect(() => {
    if (isLoadingOlderMessages) {
      return;
    }

    const anchor = prependAnchorRef.current;
    if (!anchor) {
      return;
    }

    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      prependAnchorRef.current = null;
      return;
    }

    const deltaHeight = scrollContainer.scrollHeight - anchor.scrollHeight;
    if (deltaHeight > 0) {
      scrollContainer.scrollTop = anchor.scrollTop + deltaHeight;
    }
    prependAnchorRef.current = null;
  }, [isLoadingOlderMessages, messages, scrollContainerRef]);

  useEffect(() => clearUserScrollIntent, [clearUserScrollIntent]);

  return (
    <Box
      component="main"
      ref={scrollContainerRef}
      onScroll={handleScroll}
      onWheel={markUserScrollIntent}
      onPointerDown={markUserScrollIntent}
      onTouchStart={markUserScrollIntent}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
      sx={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        backgroundColor: canvasColor,
        overflowY: "auto",
        overflowX: "hidden",
        scrollbarColor: `${scrollbarThumbColor} ${canvasColor}`,
        "&::-webkit-scrollbar": {
          backgroundColor: canvasColor,
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: canvasColor,
          border: "none",
          boxShadow: "none",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: scrollbarThumbColor,
          border: "none",
          borderRadius: 999,
        },
        "&::-webkit-scrollbar-corner": {
          backgroundColor: canvasColor,
        },
      }}
    >
      <Box
        sx={{
          maxWidth: "1000px",
          width: "100%",
          mx: "auto",
          pt: { xs: 8, sm: 9 },
          pb: showFooter ? { xs: 24, sm: 22 } : { xs: 4, sm: 4 },
        }}
      >
        {isLoadingOlderMessages ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", textAlign: "center", py: 1 }}
          >
            Loading older messages...
          </Typography>
        ) : null}
        {loading ? <ConversationSkeleton /> : null}
        {!loading
          ? messages.map((message) =>
              message.role === "user" ? (
                <UserMessage key={message.id} message={message} />
              ) : (
                <AgentMessage key={message.id} message={message} />
              )
            )
          : null}
      </Box>
    </Box>
  );
}

function ConversationSkeleton() {
  return (
    <Stack spacing={3} sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
      <Stack alignItems="flex-end">
        <Skeleton
          variant="rounded"
          width="54%"
          height={72}
          sx={{ borderRadius: 3, bgcolor: "action.hover" }}
        />
      </Stack>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Skeleton variant="circular" width={24} height={24} />
        <Stack spacing={1} sx={{ flex: 1, maxWidth: "72%" }}>
          <Skeleton variant="text" width="86%" />
          <Skeleton variant="text" width="74%" />
          <Skeleton variant="text" width="52%" />
        </Stack>
      </Stack>
    </Stack>
  );
}
